/**
 * ============================================================================
 * [P3-04] useURLs Hook
 *
 * URL 링크 데이터 관리를 위한 커스텀 훅
 *
 * 기능:
 * - 실시간 구독으로 URL 목록 동기화
 * - CRUD 함수 (addURL, updateURL, removeURL)
 *
 * Safety:
 * - useEffect cleanup으로 메모리 누수 방지
 * - 낙관적 업데이트 + 에러 시 롤백
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import {
  createURLLink,
  updateURLLink,
  deleteURLLink,
  subscribeToURLLinks,
} from '@/services/firestore/urlLinks'
import type { URLLink } from '@/types'

// ============================================================================
// 반환 타입 정의
// ============================================================================
export interface UseURLsReturn {
  // State
  urls: URLLink[]
  isLoading: boolean
  error: Error | null

  // CRUD
  addURL: (data: Omit<URLLink, 'id' | 'createdAt'>) => Promise<URLLink | null>
  updateURL: (id: string, updates: Partial<URLLink>) => Promise<boolean>
  removeURL: (id: string) => Promise<boolean>
  reorderURLs: (activeId: string, overId: string) => void
}

// ============================================================================
// useURLs Hook 구현
// ============================================================================
export function useURLs(): UseURLsReturn {
  // ========================================================================
  // Auth 상태 가져오기
  // ========================================================================
  const { user } = useAuth()

  // ========================================================================
  // Local State
  // ========================================================================
  const [urls, setUrls] = useState<URLLink[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // ========================================================================
  // 실시간 구독 useEffect
  // ========================================================================
  useEffect(() => {
    // 로그아웃 상태: 초기 상태 유지 및 구독 안함
    if (!user) {
      return
    }

    // 로딩 시작
    setIsLoading(true)

    // 구독 시작
    const unsubscribe = subscribeToURLLinks(
      user.uid,
      (newUrls) => {
        setUrls(newUrls)
        setIsLoading(false)
        setError(null)
      },
      (err) => {
        setError(err)
        setIsLoading(false)
      }
    )

    // CRITICAL: Cleanup - 메모리 누수 방지
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])
  // Note: user?.uid만 의존성에 포함하여 uid 변경 시에만 재구독

  // ========================================================================
  // addURL 함수 (Create)
  // ========================================================================
  const addURL = useCallback(
    async (data: Omit<URLLink, 'id' | 'createdAt'>): Promise<URLLink | null> => {
      if (!user) {
        console.warn('[useURLs.addURL] User not authenticated')
        return null
      }

      try {
        const newLink = await createURLLink(user.uid, data)
        setError(null)
        return newLink
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useURLs.addURL] Error:', error)
        return null
      }
    },
    [user]
  )

  // ========================================================================
  // updateURL 함수 (Update) - 낙관적 업데이트
  // ========================================================================
  const updateURL = useCallback(
    async (id: string, updates: Partial<URLLink>): Promise<boolean> => {
      if (!user) {
        console.warn('[useURLs.updateURL] User not authenticated')
        return false
      }

      // 기존 데이터 백업 (롤백용)
      const originalUrl = urls.find((u) => u.id === id)
      if (!originalUrl) {
        setError(new Error('URL not found'))
        return false
      }

      try {
        // 낙관적 업데이트 (즉시 UI 반영)
        setUrls((prev) =>
          prev.map((u) => (u.id === id ? { ...u, ...updates } : u))
        )

        // Firestore 업데이트
        await updateURLLink(user.uid, id, updates)
        setError(null)
        return true
      } catch (err) {
        // 롤백 - 원본 데이터로 복원
        setUrls((prev) =>
          prev.map((u) => (u.id === id ? originalUrl : u))
        )

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useURLs.updateURL] Error:', error)
        return false
      }
    },
    [user, urls]
  )

  // ========================================================================
  // removeURL 함수 (Delete) - 낙관적 업데이트
  // ========================================================================
  const removeURL = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        console.warn('[useURLs.removeURL] User not authenticated')
        return false
      }

      // 기존 데이터 백업 (롤백용)
      const originalUrl = urls.find((u) => u.id === id)
      if (!originalUrl) {
        setError(new Error('URL not found'))
        return false
      }

      try {
        // 낙관적 삭제 (즉시 UI 반영)
        setUrls((prev) => prev.filter((u) => u.id !== id))

        // Firestore 삭제
        await deleteURLLink(user.uid, id)
        setError(null)
        return true
      } catch (err) {
        // 롤백 - 삭제된 항목 복원
        setUrls((prev) => [...prev, originalUrl])

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useURLs.removeURL] Error:', error)
        return false
      }
    },
    [user, urls]
  )

  // ========================================================================
  // reorderURLs 함수 (드래그 앤 드롭)
  // ========================================================================
  const reorderURLs = useCallback(
    (activeId: string, overId: string) => {
      // 1. 원본 백업 (롤백용)
      const originalUrls = [...urls]

      // 2. 로컬 상태 즉시 업데이트 (낙관적 업데이트)
      const oldIndex = urls.findIndex((i) => i.id === activeId)
      const newIndex = urls.findIndex((i) => i.id === overId)

      if (oldIndex === -1 || newIndex === -1) return

      const reordered = [...urls]
      const [removed] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, removed)
      setUrls(reordered)

      // 3. Firestore 동기화
      if (!user) return

      const syncToFirestore = async () => {
        try {
          const updatePromises = reordered.map((url, index) =>
            updateURLLink(user.uid, url.id, { order: index })
          )
          await Promise.all(updatePromises)
          console.log('[useURLs] 순서 Firestore 동기화 완료')
        } catch (error) {
          console.error('[useURLs] 순서 동기화 실패, 롤백:', error)
          setUrls(originalUrls)
        }
      }
      syncToFirestore()
    },
    [urls, user]
  )

  // ========================================================================
  // Return
  // ========================================================================
  return {
    urls,
    isLoading,
    error,
    addURL,
    updateURL,
    removeURL,
    reorderURLs,
  }
}

export default useURLs
